import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type SetStateAction,
} from "react";

type DraftUpdater<T> = SetStateAction<T>;
type SaveMerge<T> = (current: T, saved: T) => T;

function canonicalize(value: unknown): unknown {
	if (value === null || value === undefined) return undefined;
	if (Array.isArray(value)) return value.map(canonicalize);
	if (typeof value !== "object") return value;

	const out: Record<string, unknown> = {};
	for (const key of Object.keys(value as Record<string, unknown>).sort()) {
		const normalized = canonicalize((value as Record<string, unknown>)[key]);
		if (normalized !== undefined) out[key] = normalized;
	}
	return out;
}

export function areDraftsEqual(left: unknown, right: unknown): boolean {
	return JSON.stringify(canonicalize(left)) === JSON.stringify(canonicalize(right));
}

export function mergeSavedEntityMetadata<
	T extends { id?: string; createdAt?: string; updatedAt?: string },
>(current: T, saved: T): T {
	return {
		...current,
		id: saved.id ?? current.id,
		createdAt: saved.createdAt ?? current.createdAt,
		updatedAt: saved.updatedAt ?? current.updatedAt,
	};
}

function resolveDraft<T>(current: T, updater: DraftUpdater<T>): T {
	if (typeof updater === "function") {
		return (updater as (value: T) => T)(current);
	}
	return updater;
}

export function useDraftState<T>(initial: T, resetKey: string): {
	draft: T;
	setDraft: (updater: DraftUpdater<T>) => void;
	adoptDraft: (updater: DraftUpdater<T>) => void;
	resetDraft: (next: T) => void;
	completeSave: (submitted: T, saved: T, mergeDirty?: SaveMerge<T>) => boolean;
	dirty: boolean;
} {
	const [draft, setDraftState] = useState<T>(initial);
	const [baseline, setBaseline] = useState<T>(initial);
	const draftRef = useRef(draft);
	const baselineRef = useRef(baseline);
	const resetKeyRef = useRef(resetKey);

	useEffect(() => {
		draftRef.current = draft;
	}, [draft]);

	useEffect(() => {
		baselineRef.current = baseline;
	}, [baseline]);

	const dirty = useMemo(() => !areDraftsEqual(draft, baseline), [draft, baseline]);

	useEffect(() => {
		const keyChanged = resetKeyRef.current !== resetKey;
		const initialChanged = !areDraftsEqual(initial, baselineRef.current);
		if (!keyChanged && !initialChanged) return;

		const pristine = areDraftsEqual(draftRef.current, baselineRef.current);
		if (!keyChanged && !pristine) return;

		resetKeyRef.current = resetKey;
		baselineRef.current = initial;
		draftRef.current = initial;
		setBaseline(initial);
		setDraftState(initial);
	}, [initial, resetKey]);

	const setDraft = useCallback((updater: DraftUpdater<T>) => {
		setDraftState((current) => {
			const next = resolveDraft(current, updater);
			draftRef.current = next;
			return areDraftsEqual(current, next) ? current : next;
		});
	}, []);

	const adoptDraft = useCallback((updater: DraftUpdater<T>) => {
		setDraftState((current) => {
			const next = resolveDraft(current, updater);
			draftRef.current = next;
			return areDraftsEqual(current, next) ? current : next;
		});
		setBaseline((current) => {
			const next = resolveDraft(current, updater);
			baselineRef.current = next;
			return areDraftsEqual(current, next) ? current : next;
		});
	}, []);

	const resetDraft = useCallback((next: T) => {
		draftRef.current = next;
		baselineRef.current = next;
		setDraftState(next);
		setBaseline(next);
	}, []);

	const completeSave = useCallback(
		(submitted: T, saved: T, mergeDirty?: SaveMerge<T>): boolean => {
			const unchanged = areDraftsEqual(draftRef.current, submitted);
			const nextDraft = unchanged
				? saved
				: mergeDirty
					? mergeDirty(draftRef.current, saved)
					: draftRef.current;
			draftRef.current = nextDraft;
			baselineRef.current = saved;
			setDraftState(nextDraft);
			setBaseline(saved);
			return unchanged;
		},
		[],
	);

	return { draft, setDraft, adoptDraft, resetDraft, completeSave, dirty };
}
